import {
  Survey,
  SurveyBroadcastStatusRequest,
  SurveyDetachRequest,
  SurveyDuplicateRequest,
  createSurvey,
  detachSurvey,
  duplicateSurvey,
  getSurvey,
  listSurveys,
  updateSurvey
} from '@goldcast/api/backend';

import { processSurveyData } from './utils';

import { showErrorNotification } from '@/utils/utils';

const actions = {
  async fetchSurveys({ commit }) {
    commit('setIsSurveysLoading', true);

    try {
      const surveys = await listSurveys({
        queryParams: {
          duplicated__isnull: true
        }
      });

      const processedSurveys =
        Array.isArray(surveys) &&
        surveys.map(survey => {
          return processSurveyData(survey);
        });

      commit('setSurveys', processedSurveys);
    } catch (error) {
      showErrorNotification('Error fetching Surveys, please try again!');
    } finally {
      commit('setIsSurveysLoading', false);
    }
  },
  async updateSurveyById({ commit, rootGetters }, { survey, broadcastId }: { survey: Survey; broadcastId: string }) {
    try {
      const surveyResponse = await updateSurvey({
        id: survey.id,
        body: {
          ...survey,
          broadcast: broadcastId
        }
      });

      const processedSurvey = processSurveyData(surveyResponse);

      const allSurveys = rootGetters['studioSurveys/getSurveys'];

      const isSurveyPresent = allSurveys.some((survey: Survey) => survey.id === processedSurvey.id);

      if (isSurveyPresent) {
        commit('updateSurvey', processedSurvey);
      } else {
        commit('addSurvey', processedSurvey);
      }
      return processedSurvey;
    } catch (error) {
      showErrorNotification('Error updating Survey, please try again!');
      throw error;
    }
  },
  async createNewSurvey({ commit }, { survey, broadcastId }: { survey: Survey; broadcastId?: string }) {
    const body = {
      ...survey,
      broadcast: broadcastId
    } as SurveyBroadcastStatusRequest;

    try {
      const survey = await createSurvey({
        body
      });

      const processedSurvey = processSurveyData(survey);

      commit('addSurvey', processedSurvey);

      return processedSurvey;
    } catch (error) {
      showErrorNotification('Error creating Survey, please try again!');
      throw error;
    }
  },
  async duplicateSurveyById(_, { surveyId, broadcastId }: { surveyId: string; broadcastId?: string }) {
    const body = {
      broadcast: broadcastId
    } as SurveyDuplicateRequest;

    try {
      const survey = await duplicateSurvey({
        id: surveyId,
        body
      });

      const processedSurvey = processSurveyData(survey);
      return processedSurvey;
    } catch (error) {
      showErrorNotification('Error duplicating Survey, please try again!');
      throw error;
    }
  },
  async fetchSurveyById(_, surveyId: string) {
    try {
      const survey = await getSurvey({
        id: surveyId
      });

      const processedSurvey = processSurveyData(survey);
      return processedSurvey;
    } catch (error) {
      showErrorNotification('Error fetching Survey, please try again!');
    }
  },
  async detachSurveyById({ commit }, { surveyId, broadcastId }: { surveyId: string; broadcastId?: string }) {
    const body = {
      broadcast: broadcastId
    } as SurveyDetachRequest;

    commit('setIsDeleteSurveyLoading', true);

    try {
      await detachSurvey({
        id: surveyId,
        body
      });
    } catch (error) {
      const apiErrorCode = (error as { apiErrorCode?: string })?.apiErrorCode;
      if (apiErrorCode === 'SURVEY_HAS_DUPLICATED_SURVEYS') {
        showErrorNotification('You cannot delete the survey since it is being used in other broadcasts.');
      } else {
        showErrorNotification('Error detaching Survey, please try again!');
      }
      throw error;
    } finally {
      commit('setIsDeleteSurveyLoading', false);
    }
  }
};

export default actions;
